import { Box, Chip, IconButton, Typography } from "@mui/material"
import { MailOutlined, MoreVertOutlined, PhoneIphoneOutlined } from "@mui-symbols-material/w300"
import React from "react"
import { twMerge } from "tailwind-merge"

import type { Contact, ContactMethod } from "@/graphql/codegen/graphql"
import { ContactChannel } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import ExpandedLocationRowDropdownMenu from "@/screens/Companies/edit/components/locations/ExpandedLocationRowDropdownMenu"
import { useRemoveContactLocation } from "@/screens/Contacts/edit/hooks/useRemoveContactLocation"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface ExpandedLocationRowContactRowProps {
  contact: Contact
  handleMenuOpen: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
  handleMenuClose: () => void
  anchorEl: HTMLElement | null
  locationId: string
  className?: string
  onRemoveSuccess?: () => void
}

const contactMethodOrder = [ContactChannel.Email, ContactChannel.Phone]

const ContactCard: React.FC<ExpandedLocationRowContactRowProps> = ({
  contact,
  handleMenuOpen,
  handleMenuClose,
  anchorEl,
  className,
  locationId,
  onRemoveSuccess,
}) => {
  const { removeContactLocation } = useRemoveContactLocation()
  const fullName = `${contact?.firstName} ${contact?.lastName}`
  const sortedContactMethods = contact.contactMethod?.sort((a, b) => {
    const aIndex = contactMethodOrder.indexOf(a?.contactChannel as ContactChannel)
    const bIndex = contactMethodOrder.indexOf(b?.contactChannel as ContactChannel)
    return aIndex - bIndex
  })
  const notify = useNotificationsStore(({ enqueueNotification }) => enqueueNotification)

  const isPreferredContactMethod = ({ sortOrder }: ContactMethod) => sortOrder === 0

  const handleRemoveFromLocation = async () => {
    const contactLocationId = contact.contactLocations.find((cl) => cl.locationId === locationId)?.contactLocationId

    try {
      if (contactLocationId) {
        await removeContactLocation({
          input: {
            contactLocationId,
          },
        })

        onRemoveSuccess?.()

        notify({
          type: NotificationType.Success,
          message: "Contact removed from location successfully",
        })
      }
    } catch (error) {
      notify({
        type: NotificationType.Error,
        message: "Error removing contact from location",
      })
      console.error("Error removing contact from location:", error)
    }

    handleMenuClose()
  }

  return (
    <Box
      className={twMerge(["mb-4 grid grid-cols-4 justify-between rounded-md border border-gray-300 p-4", className])}
    >
      <Box className='flex flex-col items-start'>
        <Typography component='p' className='capitalize text-gray-900'>
          {fullName ?? ""}
        </Typography>
        {contact.title && (
          <Typography component='p' className='capitalize text-gray-900'>
            {contact.title}
          </Typography>
        )}
      </Box>
      <Box className='flex flex-col items-start justify-center'>
        {sortedContactMethods?.map((contactMethod) => {
          const isContactVisible = contactMethod?.contactChannel !== null && !!contactMethod?.value
          if (isContactVisible) {
            switch (contactMethod?.contactChannel) {
              case ContactChannel.Email:
                return (
                  <Typography
                    key={`${contactMethod.contactChannel}-${contactMethod.value}`}
                    component='p'
                    className='mb-1 flex break-all align-middle text-gray-700'
                  >
                    <MailOutlined className='mr-2' />
                    {contactMethod.value}
                    {isPreferredContactMethod(contactMethod) && ` (${contactMethod.contactType})`}
                  </Typography>
                )
              case ContactChannel.Phone:
                return (
                  <Typography key={contactMethod.contactChannel} component='p' className='capitalize text-gray-700'>
                    <PhoneIphoneOutlined className='mr-2' color='disabled' />
                    {contactMethod.value}
                    {isPreferredContactMethod(contactMethod) && ` (${contactMethod.contactType})`}
                  </Typography>
                )
              default:
                return null
            }
          }
          return null
        })}
      </Box>
      <Box className='flex flex-row items-center'>
        <Box className='flex flex-wrap gap-2'>
          {contact.contactSubscriptions.map((subscription) => (
            <Chip
              key={String(subscription?.name)}
              label={String(subscription?.name)}
              className='mr-1 rounded-sm bg-tertiary-yellow-300 font-[12px] text-gray-900'
            />
          ))}
        </Box>
      </Box>
      <Box className='flex size-min self-center justify-self-end'>
        <IconButton onClick={(e) => handleMenuOpen(e)}>
          <MoreVertOutlined />
        </IconButton>
        <ExpandedLocationRowDropdownMenu
          anchorEl={anchorEl}
          onClose={handleMenuClose}
          onRemoveFromLocation={handleRemoveFromLocation}
          contactId={contact.contactId!}
        />
      </Box>
    </Box>
  )
}

export default ContactCard
